import * as z from "zod";
import { emailAddress as address } from "../email-address";
import { decimal } from "../index";

// How a dataset of reference data is described (ADR-077): its fields with their formats, once. The worker validates
// and writes from this description, the gateway documents it, and the app draws the grid and the form of every
// dataset from it: a new list is one entry in `datasets.ts`, not a screen.

/** What a field holds, which is also the control that edits it. */
export type FieldKind = "text" | "integer" | "decimal" | "boolean" | "date" | "choice" | "reference" | "references";

export interface Field<S extends z.ZodType = z.ZodType> {
  kind: FieldKind;
  /** The format of the value, the same in the form, at the gateway and in the domain. */
  schema: S;
  /** Whether the field can be left empty (`null`). */
  nullable: boolean;
  /** `text`: the longest value the column takes. */
  maxLength?: number;
  /** `choice`: the values offered. */
  options?: readonly string[];
  /** `reference` and `references`: the dataset whose records are offered. */
  references?: string;
  /** What the form of a new record starts with: the default of the column. */
  initial?: string | number | boolean;
}

export type Fields = Record<string, Field>;

/** The values of a record: every field of the dataset, the empty ones as `null`. */
export type ValuesSchema<F extends Fields> = z.ZodObject<{ [N in keyof F]: F[N]["schema"] }>;

/** Where the administration shows a dataset. */
export const datasetGroups = ["product", "campaign", "commercial", "company"] as const;
export type DatasetGroup = (typeof datasetGroups)[number];

export interface Dataset<K extends string = string, F extends Fields = Fields> {
  /** The name of its table in the `reference` schema. */
  key: K;
  group: DatasetGroup;
  /**
   * The format of a natural key chosen by who creates the record and never changed (a currency or a country code);
   * null when the id is a uuid v7 from the client, as for every other entity (ADR-046).
   */
  code: z.ZodString | null;
  fields: F;
  values: ValuesSchema<F>;
  /** The fields that name a record where another one references it, in order. */
  label: readonly (keyof F & string)[];
}

/** `fields` in the order the grid and the form show them. */
export function dataset<const K extends string, F extends Fields>(spec: {
  key: K;
  group: DatasetGroup;
  code?: z.ZodString;
  label: readonly (keyof F & string)[];
  fields: F;
}): Dataset<K, F> {
  const shape = Object.fromEntries(Object.entries(spec.fields).map(([name, field]) => [name, field.schema]));
  return {
    key: spec.key,
    group: spec.group,
    code: spec.code ?? null,
    fields: spec.fields,
    values: z.object(shape).meta({ id: `Reference${pascalCase(spec.key)}` }) as unknown as ValuesSchema<F>,
    label: spec.label,
  };
}

const pascalCase = (key: string) => key.replace(/(^|_)([a-z])/g, (_, __, letter: string) => letter.toUpperCase());

function field<S extends z.ZodType>(
  kind: FieldKind,
  schema: S,
  extra: Pick<Field, "maxLength" | "options" | "references" | "initial"> = {},
): Field<S> {
  return { kind, schema, nullable: false, ...extra };
}

/** A text of at most `max` characters, trimmed; `pattern` for the codes with a fixed form. */
export function text(max: number, pattern?: RegExp) {
  const schema = z.string().trim().min(1).max(max);
  return field("text", pattern ? schema.regex(pattern) : schema, { maxLength: max });
}

/** An email address, kept in lowercase. */
export const emailAddress = () => field("text", address, { maxLength: 320 });

export const integer = (min: number, max: number, initial?: number) =>
  field("integer", z.number().int().min(min).max(max), initial === undefined ? {} : { initial });

/** A percentage between 0 and 100 with at most two decimals, as text (`numeric(5,2)`). */
export const percentage = () =>
  field(
    "decimal",
    decimal.regex(/^\d{1,3}(\.\d{1,2})?$/).refine((value) => Number(value) <= 100),
  );

export const flag = (initial = false) => field("boolean", z.boolean(), { initial });

export const day = () => field("date", z.iso.date());

export function choice<const O extends readonly [string, ...string[]]>(options: O, initial?: O[number]) {
  return field("choice", z.enum(options), initial === undefined ? { options } : { options, initial });
}

/** A record of another dataset, by its uuid. */
export const ref = (references: string) => field("reference", z.uuid(), { references });

/** A record of a dataset with a natural key, by its code. */
export const codeRef = (references: string, code: z.ZodString) => field("reference", code, { references });

/** Records of another dataset, in the order given, each at most once. */
export const refs = (references: string) =>
  field(
    "references",
    z
      .array(z.uuid())
      .max(200)
      .refine((ids) => new Set(ids).size === ids.length),
    { references },
  );

export function nullable<S extends z.ZodType>(of: Field<S>): Field<z.ZodNullable<S>> {
  return { ...of, schema: of.schema.nullable(), nullable: true };
}
